import React, { Suspense } from 'react';
import { Redirect, Route, Switch } from 'react-router-dom';
import { Application, Loading, Module, Privilege } from '@fides/fides-react-components';
import { computePaymentPermissions } from 'components/pages/Payment/utils/paymentPermissions';
import { useGlobalConfigContext } from 'core/contexts/GlobalConfig';
import { PrivateRoute } from 'core/helpers';

const CreditTransfersAll = React.lazy(() => import('../../components/pages/CreditTransfers/All/CreditTransfersGrid'));
const CreditTransfersDeleted = React.lazy(() => import('../../components/pages/CreditTransfers/Deleted'));
const CreditTransfersUploadedFiles = React.lazy(() => import('../../components/pages/CreditTransfers/UploadedFiles'));
const PaymentTransactions = React.lazy(() => import('../../components/pages/PaymentTransactions'));
const DbStatusHistory = React.lazy(() => import('../../components/pages/DbStatusHistory'));
const PaymentView = React.lazy(() => import('../../components/pages/Payment/pages/PaymentView/PaymentViewPage'));
const PaymentAdd = React.lazy(() => import('../../components/pages/Payment/pages/PaymentAddPage'));
const PaymentEdit = React.lazy(() => import('../../components/pages/Payment/pages/PaymentEditPage'));
const RegulatoryReportingEditor = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/RegulatoryReporting/RegulatoryReportingDetails/RegulatoryReportingEditor'
    )
);
const RegulatoryReportingView = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/RegulatoryReporting/RegulatoryReportingDetails/RegulatoryReportingView'
    )
);
const StructuredRemittanceInformationEditor = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/StructuredRemittanceInformation/StructuredRemittanceInformationDetails/StructuredRemittanceInformationEditor'
    )
);
const StructuredRemittanceInformationView = React.lazy(
  () =>
    import(
      '../../components/pages/Payment/pages/StructuredRemittanceInformation/StructuredRemittanceInformationDetails/StructuredRemittanceInformationView'
    )
);

const AccessGuard: React.FC<{ hasAccess: boolean; children: React.ReactNode }> = ({ hasAccess, children }) =>
  // eslint-disable-next-line react/jsx-no-useless-fragment
  hasAccess ? <>{children}</> : <Redirect to='/page-not-found' />;

const getRedirectPath = (
  hasAccessToAll: boolean,
  hasAccessToUploaded: boolean,
  hasAccessToDeleted: boolean
): string => {
  if (hasAccessToAll) {
    return '/payments/all';
  }
  if (hasAccessToUploaded) {
    return '/payments/uploaded-files';
  }
  if (hasAccessToDeleted) {
    return '/payments/deleted';
  }
  return '/page-not-found';
};

export const CreditTransfersRouter = () => {
  const { user, accessHelper, hasUserAppAccess } = useGlobalConfigContext();
  const hasAuthorityName = accessHelper.hasAuthorityName.bind(accessHelper);

  const hasEftwebAccess = hasUserAppAccess(Application.EFTWEB);
  const isImpersonatedClient = accessHelper.isImpersonatedAsClient;

  const hasAccessToAll =
    hasEftwebAccess &&
    (accessHelper.hasPermissionForModule(Module.PAYMENT_VIEW, Privilege.GRANT) || isImpersonatedClient);
  const hasAccessToUploaded =
    hasEftwebAccess &&
    (accessHelper.hasPermissionForModule(Module.PAYMENT_VIEW_UPLOADED, Privilege.GRANT) || isImpersonatedClient);
  const hasAccessToDeleted =
    hasEftwebAccess &&
    (accessHelper.hasPermissionForModule(Module.VIEW_DELETED_ORDER, Privilege.GRANT) || isImpersonatedClient);
  const hasAccessToView = hasAccessToAll || hasAccessToUploaded || hasAccessToDeleted;

  const hasPaymentAccess = hasAuthorityName('PAYMENT_CREATE_ANY_GRANT') && !user?.impersonated;
  const hasTemplateAccess = hasAuthorityName('PAYMENT_CREATE_FROM_TEMPLATE_GRANT') && !user?.impersonated;
  const hasAccessToEditor =
    (hasAuthorityName('PAYMENT_CREATE_ANY_GRANT') || hasAuthorityName('PAYMENT_CREATE_FROM_TEMPLATE_GRANT')) &&
    !user?.impersonated;
  const hasAccessToEdit =
    !user?.impersonated && computePaymentPermissions(hasAuthorityName).canAccessAnyPaymentEditByPermissions;

  return (
    <Switch>
      <Redirect from='/payments/' to={getRedirectPath(hasAccessToAll, hasAccessToUploaded, hasAccessToDeleted)} exact />
      <Route path='/payments/all' exact>
        <Suspense fallback={<Loading />}>
          <AccessGuard hasAccess={hasAccessToAll}>
            <CreditTransfersAll />
          </AccessGuard>
        </Suspense>
      </Route>
      <Route path='/payments/deleted' exact>
        <Suspense fallback={<Loading />}>
          <AccessGuard hasAccess={hasAccessToDeleted}>
            <CreditTransfersDeleted />
          </AccessGuard>
        </Suspense>
      </Route>
      <Route path='/payments/uploaded-files' exact>
        <Suspense fallback={<Loading />}>
          <AccessGuard hasAccess={hasAccessToUploaded}>
            <CreditTransfersUploadedFiles />
          </AccessGuard>
        </Suspense>
      </Route>
      <Route path='/payments/transactions' exact>
        <Suspense fallback={<Loading />}>
          <AccessGuard hasAccess={hasAccessToAll}>
            <PaymentTransactions />
          </AccessGuard>
        </Suspense>
      </Route>
      <Route path='/payments/db-status-history' exact>
        <Suspense fallback={<Loading />}>
          <AccessGuard hasAccess={hasAccessToAll}>
            <DbStatusHistory />
          </AccessGuard>
        </Suspense>
      </Route>
      <PrivateRoute path='/payments/add/' exact>
        <AccessGuard hasAccess={hasPaymentAccess}>
          <PaymentAdd />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/add/:templateId?' exact>
        <AccessGuard hasAccess={hasTemplateAccess}>
          <PaymentAdd />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/regulatoryReportingDetails/editor/:uniqueId?' exact>
        <AccessGuard hasAccess={hasAccessToEditor}>
          <RegulatoryReportingEditor />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/regulatoryReportingDetails/:uniqueId' exact>
        <AccessGuard hasAccess={hasAccessToView}>
          <RegulatoryReportingView />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/structuredRemittanceInformation/:action(add|edit)/:uniqueId?' exact>
        <AccessGuard hasAccess={hasAccessToEditor}>
          <StructuredRemittanceInformationEditor />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/structuredRemittanceInformation/:uniqueId' exact>
        <AccessGuard hasAccess={hasAccessToView}>
          <StructuredRemittanceInformationView />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/:uniqueId' exact>
        <AccessGuard hasAccess={hasAccessToView}>
          <PaymentView />
        </AccessGuard>
      </PrivateRoute>
      <PrivateRoute path='/payments/:uniqueId/edit' exact>
        <AccessGuard hasAccess={hasAccessToEdit}>
          <PaymentEdit />
        </AccessGuard>
      </PrivateRoute>
      <Redirect to='/page-not-found' />
    </Switch>
  );
};
